import { TopNavCreateActions } from '@/components/buttons/TopNavCreateActions';
import AskAiButton from '@/components/buttons/viewHeaderButtons/AskAiButton';
import ActivitiesContent from '@/components/Drawers/PreviewDrawer/ActivitiesContent';
import ClientDrawerContent from '@/components/Drawers/PreviewDrawer/ClientDrawerContent';
import DealDrawerContent from '@/components/Drawers/PreviewDrawer/DealDrawerContent';
import PreviewDrawerTabHeader from '@/components/Drawers/PreviewDrawer/PreviewDrawerTabHeader';
import { FileUploadMSGraph } from '@/components/FileUploadMSGraph';
import { FolioErrorBoundary } from '@/components/layouts/FolioErrorBoundary';
import { AttachmentsContextProvider } from '@/context/attachmentsContext';
import { usePipelineContext } from '@/context/pipelineContext';
import { getPipelineName } from '@/components/dialogs/CompleteDealDialog/data';
import { useObjectContext } from '@/hooks/useObjectContext';
import { usePreviewClickOutside } from '@/hooks/usePreviewMode';
import { Box, CircularProgress, Divider, Fade, Grid, Stack, Typography } from '@mui/material';
import React, { useMemo, useRef } from 'react';

const PreviewDrawerContent = () => {
	const drawerRef = useRef(null);
	usePreviewClickOutside(drawerRef);

	const { clientRow, insight, broker, dealRow, isClientView, isClientLoading } =
		useObjectContext();
	const { pipeline } = usePipelineContext(dealRow?.pipeline);

	const client = useMemo(() => ({ clientRow, insight, broker }), [clientRow, insight, broker]);
	const isProspect = clientRow?.isProspect;
	const isPreviewLoading = isClientView ? isClientLoading : !dealRow || isClientLoading;

	const detailsTitle = useMemo(() => {
		const type = isClientView
			? isProspect
				? 'Prospect'
				: 'Client'
			: getPipelineName(pipeline) || 'Deal';

		return `${type} details`;
	}, [isClientView, isProspect, pipeline]);

	return (
		<Box ref={drawerRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 2 }}>
			{isPreviewLoading ? (
				<Box display='flex' justifyContent='center' py={8}>
					<CircularProgress />
				</Box>
			) : (
				<Fade in timeout={300}>
					<Box>
						<AttachmentsContextProvider
							{...(!isClientView && { deal: dealRow })}
							client={client}
							displayCreateFolder={false}
							promptFolderSelectOnUpload
							renderUpload={false}
						>
							<PreviewDrawerTabHeader />

							<Divider sx={{ my: 2 }} />
							<Stack spacing={1.5}>
								<Stack sx={{ px: 2 }}>
									<FolioErrorBoundary
										{...(!isClientView && { dealId: dealRow?.dealId })}
										clientId={clientRow?.clientId}
										key={`preview-mode-${clientRow?.clientId}`}
										message='Error loading file upload'
										context='FileUploadMSGraph'
									>
										<FileUploadMSGraph />
									</FolioErrorBoundary>

									<Stack
										direction='row'
										width={'100%'}
										spacing={1.5}
										sx={{
											justifyContent: 'space-around',
											'& .MuiButtonBase-root': {
												textTransform: 'none',
												whiteSpace: 'nowrap',
												flexShrink: 0,
											},
										}}
									>
										<TopNavCreateActions />
										<AskAiButton minimal />
									</Stack>
								</Stack>
								<Divider />

								<Typography variant='subtitle1' sx={{ px: 2 }}>
									{detailsTitle}
								</Typography>

								<Grid
									container
									rowSpacing={1}
									columnSpacing={2}
									sx={{
										'& .MuiGrid-root': {
											display: 'flex',
											alignItems: 'flex-end',
										},
										px: 2,
									}}
								>
									{isClientView ? <ClientDrawerContent /> : <DealDrawerContent />}
								</Grid>
								<Grid container spacing={1} sx={{ px: 2 }}>
									<ActivitiesContent />
								</Grid>
							</Stack>
						</AttachmentsContextProvider>
					</Box>
				</Fade>
			)}
		</Box>
	);
};
PreviewDrawerContent.displayName = 'PreviewDrawerContent';
export default PreviewDrawerContent;
